import {
  FileDirectoryIcon,
  SearchIcon,
} from "@primer/octicons-react";
import { Box } from "@primer/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ExcelJS from "exceljs";
import {
  FO_BAOCAO_AN_HOC_SINH_API_END_POINT,
  foDangKyAnHocSinhApi,
} from "../../../api/foDangKyAnHocSinhApi";
import ComboboxDotDangKyAn from "../../../components/combobox-dot-dang-ky-an/ComboboxDotDangKyAn";
import ComboboxHe from "../../../components/combobox-he/ComboboxHe";
import ComboboxKhoi from "../../../components/combobox-khoi/ComboboxKhoi";
import { ComboboxTruong } from "../../../components/combobox-truong";
import HocSinhShortInfo from "../../../components/hoc-sinh/HocSinhShortInfo";
import Button from "../../../components/ui/button";
import DataGrid from "../../../components/data-grid/DataGrid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import Text from "../../../components/ui/text";
import MyTextInput from "../../../components/ui/text-input";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { useDangKyAnHocSinh } from "../../../hooks/useDangKyAnHocSinh";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { IFoThucDonItemRequest } from "../../../models/request/thuc-don/IFoThucDonItemRequest";

const BaoCaoNgayPage = () => {
  const dispatch = useDispatch();
  const { dm_coso_id, nam_hoc, dm_truong_id } = useCommonSelectedHook();
  const { translate, checkAccesiableTo } = useCommonContext();
  const { loadAllWithParams, status } = useDangKyAnHocSinh();

  const [foBaoCaoAns, setFoBaoCaoAns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterTruongId, setFilterTruongId] = useState<number>(0);
  const [filterKhoiId, setFilterKhoiId] = useState<number>(0);
  const [filterHeId, setFilterHeId] = useState<number>(0);
  const [filterDotDangKyId, setFilterDotDangKyId] = useState<number>(0);

  const canView = checkAccesiableTo(`${FO_BAOCAO_AN_HOC_SINH_API_END_POINT}/ngay`, "POST");

  const [dataLoad, setDataLoad] = useState<IFoThucDonItemRequest>({
    dm_coso_id,
    dm_truong_id,
    from_date: new Date().toISOString().split("T")[0],
    to_date: new Date().toISOString().split("T")[0],
  });

  const handleDateChange = (field: "from_date" | "to_date") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDataLoad((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSearchUserAsync = useCallback(async () => {
    if (!dm_coso_id || !nam_hoc || !dataLoad.from_date || !dataLoad.to_date) return;

    setLoading(true);
    try {
      const res = await foDangKyAnHocSinhApi.select_by_baocao({
        dm_coso_id,
        dm_truong_id: filterTruongId,
        fo_dotdangky_an_id: filterDotDangKyId,
        nam_hoc,
        from_date: dataLoad.from_date,
        to_date: dataLoad.to_date,
      });

      const resDiemDanh = await foDangKyAnHocSinhApi.select_by_baocao_diemdanh({
        dm_coso_id,
        dm_truong_id: filterTruongId,
        fo_dotdangky_an_id: filterDotDangKyId,
        nam_hoc,
        from_date: dataLoad.from_date,
        to_date: dataLoad.to_date,
      });

      if (res.is_success && resDiemDanh.is_success) {
        const rawData = res.data || [];
        const diemDanhData = resDiemDanh.data || [];

        const diemDanhMap: Record<number, any> = {};
        diemDanhData.forEach((item: any) => {
          diemDanhMap[item.ts_hocsinh_id] = {
            id: item.sis_diemdanh_status_id,
            name: item.sis_diemdanh_status_name,
          };
        });

        const processed = rawData.map((item: any) => ({
          ...item,
          sis_diemdanh_status_id: diemDanhMap[item.ts_hocsinh_id]?.id ?? null,
          diem_danh: diemDanhMap[item.ts_hocsinh_id]?.name || "-",
        }));

        setFoBaoCaoAns(processed);
      }
    } finally {
      setLoading(false);
    }
  }, [dm_coso_id, dm_truong_id, nam_hoc, dataLoad, filterTruongId, filterDotDangKyId]);

  const filteredData = useMemo(() => {
    return foBaoCaoAns.filter((item) => {
      return (
        (!filterDotDangKyId || item.fo_dotdangky_an_id === filterDotDangKyId) &&
        (!filterTruongId || item.dm_truong_id === filterTruongId) &&
        (!filterKhoiId || item.dm_khoi_id === filterKhoiId) &&
        (!filterHeId || item.dm_he_id === filterHeId)
      );
    });
  }, [foBaoCaoAns, filterDotDangKyId, filterTruongId, filterKhoiId, filterHeId]);

  const handleExportBaoComExcel = async () => {
    if (!filteredData || !filteredData.length) {
      alert("Không có dữ liệu để xuất");
      return;
    }

    const validData = filteredData.filter((x: any) => {
      // Ưu tiên so sánh trực tiếp mã gốc
      const maBanTru = (x.ma_ban_tru || "").toUpperCase();

      return (
        ["CAS", "KAS"].includes(maBanTru) &&
        [1, 2].includes(x.sis_diemdanh_status_id)
      );
    });

    if (!validData.length) {
      alert("Không có dữ liệu thỏa mãn điều kiện để xuất");
      return;
    }

    // Danh sách tất cả tên khối có trong dữ liệu
    const allKhoiNames = Array.from(
      new Set(
        validData
          .map((x: any) => (x.ten_khoi || "").trim())
          .filter((k: string) => Boolean(k))
      )
    );

    // Xác định tên khối chuẩn cho lớp
    const getCanonicalKhoi = (tenLop: string, fallbackKhoi?: string): string => {
      const trimmedLop = (tenLop || "").trim();
      const match = trimmedLop.match(/^(\d+)/) || trimmedLop.match(/^[Ll][ớơo]p\s*(\d+)/i);
      if (match) {
        const gradeNum = match[1];
        const matched = allKhoiNames.find((k) => {
          const m = k.match(/\d+/);
          return m && m[0] === gradeNum;
        });
        if (matched) return matched;
        return `Khối ${gradeNum}`;
      }
      return (fallbackKhoi || "").trim() || "Khác";
    };

    // Gom lớp và đếm số lượng
    const classMap: Record<
      string,
      { ten_lop: string; rawKhoiCounts: Record<string, number>; so_luong: number }
    > = {};

    validData.forEach((cur: any) => {
      const tenLop = (cur.ten_lop || "").trim();
      if (!tenLop) return;

      if (!classMap[tenLop]) {
        classMap[tenLop] = {
          ten_lop: tenLop,
          rawKhoiCounts: {},
          so_luong: 0,
        };
      }
      classMap[tenLop].so_luong += 1;

      const rawKhoi = (cur.ten_khoi || "").trim();
      if (rawKhoi) {
        classMap[tenLop].rawKhoiCounts[rawKhoi] =
          (classMap[tenLop].rawKhoiCounts[rawKhoi] || 0) + 1;
      }
    });

    const summaryByClass = Object.values(classMap).map((item) => {
      let mostFrequentRawKhoi = "";
      let maxCount = 0;
      Object.entries(item.rawKhoiCounts).forEach(([khoi, count]) => {
        if (count > maxCount) {
          maxCount = count;
          mostFrequentRawKhoi = khoi;
        }
      });

      const tenKhoi = getCanonicalKhoi(item.ten_lop, mostFrequentRawKhoi);
      return {
        ten_khoi: tenKhoi,
        ten_lop: item.ten_lop,
        so_luong: item.so_luong,
      };
    });

    // Gom khối từ summaryByClass để đảm bảo tổng khối khớp hoàn toàn với tổng các lớp
    const khoiMap: Record<string, number> = {};
    summaryByClass.forEach((cls) => {
      khoiMap[cls.ten_khoi] = (khoiMap[cls.ten_khoi] || 0) + cls.so_luong;
    });

    const summaryByKhoi = Object.entries(khoiMap).map(([ten_khoi, so_luong]) => ({
      ten_khoi,
      so_luong,
    }));

    // Sort
    const sortedKhoi = [...summaryByKhoi].sort((a, b) =>
      (a.ten_khoi || "").localeCompare(b.ten_khoi || "", "vi", { numeric: true })
    );

    const sortedClass = [...summaryByClass].sort(
      (a, b) =>
        (a.ten_khoi || "").localeCompare(b.ten_khoi || "", "vi", { numeric: true }) ||
        (a.ten_lop || "").localeCompare(b.ten_lop || "", "vi", { numeric: true })
    );

    // Gom lớp theo khối
    const groupedByKhoi: Record<string, typeof sortedClass> = {};
    sortedClass.forEach((item) => {
      if (!groupedByKhoi[item.ten_khoi]) groupedByKhoi[item.ten_khoi] = [];
      groupedByKhoi[item.ten_khoi].push(item);
    });

    const khoiList = sortedKhoi.map((k) => k.ten_khoi);

    // Số dòng tối đa (nhiều lớp nhất)
    const maxRows = Math.max(
      0,
      ...Object.values(groupedByKhoi).map((arr) => arr.length)
    );

    // Xác định cấp học và màu sắc cho từng khối:
    // TiH (Khối 1 - 5): Vàng (FFFF00)
    // THCS (Khối 6 - 9): Xanh lá (92D050)
    // THPT (Khối 10 - 12): Xanh dương nhạt (9BC2E6)
    const getGradeInfo = (khoiName: string) => {
      const match = (khoiName || "").match(/\d+/);
      const gradeNum = match ? parseInt(match[0], 10) : 0;
      if (gradeNum >= 1 && gradeNum <= 5) {
        return { category: "TiH", color: "FFFFFF00" };
      }
      if (gradeNum >= 6 && gradeNum <= 9) {
        return { category: "THCS", color: "FF92D050" };
      }
      if (gradeNum >= 10 && gradeNum <= 12) {
        return { category: "THPT", color: "FF9BC2E6" };
      }
      return { category: "KHAC", color: "FFE7E6E6" };
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("BaoCom", {
      views: [{ showGridLines: true }],
    });

    const dateStr = dataLoad?.from_date
      ? dataLoad.from_date.split("-").reverse().join(".")
      : "";

    // Dòng 1: Tiêu đề báo cáo
    const titleCell = worksheet.getCell("A1");
    titleCell.value = `BÁO CƠM BỮA TRƯA ${dateStr}`;
    titleCell.font = { name: "Calibri", size: 11, bold: true };

    // Dòng 3: Tiêu đề phân mục
    const labelLop = worksheet.getCell("A3");
    labelLop.value = "THỐNG KÊ THEO LỚP";
    labelLop.font = { name: "Calibri", size: 11, bold: true };

    // Bảng khối đặt bên phải bảng lớp, cách 1 cột trống
    const khoiColStart = khoiList.length * 2 + 2;

    worksheet.mergeCells(3, khoiColStart, 3, khoiColStart + 1);
    const labelKhoi = worksheet.getCell(3, khoiColStart);
    labelKhoi.value = "THỐNG KÊ THEO KHỐI";
    labelKhoi.font = { name: "Calibri", size: 11, bold: true };
    labelKhoi.alignment = { horizontal: "center", vertical: "middle" };

    // Header bảng khối (Dòng 4)
    const hKhoiCol1 = worksheet.getCell(4, khoiColStart);
    hKhoiCol1.value = "Khối";
    hKhoiCol1.font = { name: "Calibri", size: 10, bold: true };
    hKhoiCol1.alignment = { horizontal: "center", vertical: "middle" };

    const hKhoiCol2 = worksheet.getCell(4, khoiColStart + 1);
    hKhoiCol2.value = "Số lượng";
    hKhoiCol2.font = { name: "Calibri", size: 10, bold: true };
    hKhoiCol2.alignment = { horizontal: "center", vertical: "middle" };

    const thinBorder: Partial<ExcelJS.Borders> = {
      top: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
    };

    // Header bảng lớp (Dòng 4) & Data (Dòng 5 trở đi)
    khoiList.forEach((khoi, idx) => {
      const colStart = 1 + idx * 2;
      const colEnd = colStart + 1;
      const info = getGradeInfo(khoi);

      // Merge header của khối
      worksheet.mergeCells(4, colStart, 4, colEnd);
      const hCell = worksheet.getCell(4, colStart);
      hCell.value = khoi.toUpperCase();
      hCell.font = { name: "Calibri", size: 10, bold: true };
      hCell.alignment = { horizontal: "center", vertical: "middle" };

      [colStart, colEnd].forEach((c) => {
        const cell = worksheet.getCell(4, c);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: info.color },
        };
        cell.border = thinBorder;
      });

      // Dữ liệu các lớp và các ô trống nền màu đồng bộ
      const lopList = groupedByKhoi[khoi] || [];
      for (let r = 0; r < maxRows; r++) {
        const rowNum = 5 + r;
        const item = lopList[r];

        const cellLop = worksheet.getCell(rowNum, colStart);
        cellLop.value = item ? item.ten_lop : "";
        cellLop.font = { name: "Calibri", size: 10 };
        cellLop.alignment = { horizontal: "left", vertical: "middle" };
        cellLop.border = thinBorder;
        cellLop.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: info.color },
        };

        const cellQty = worksheet.getCell(rowNum, colEnd);
        cellQty.value = item ? item.so_luong : "";
        cellQty.font = { name: "Calibri", size: 10 };
        cellQty.alignment = { horizontal: "right", vertical: "middle" };
        cellQty.border = thinBorder;
        cellQty.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: info.color },
        };
      }

      // Độ rộng cột bảng lớp
      worksheet.getColumn(colStart).width = 7.5;
      worksheet.getColumn(colEnd).width = 6.5;
    });

    // Cột khoảng trắng ngăn cách và độ rộng cột bảng khối
    worksheet.getColumn(khoiColStart - 1).width = 4;
    worksheet.getColumn(khoiColStart).width = 14;
    worksheet.getColumn(khoiColStart + 1).width = 12;

    // Điền dữ liệu bảng THỐNG KÊ THEO KHỐI
    const tihList = sortedKhoi.filter((k) => getGradeInfo(k.ten_khoi).category === "TiH");
    const thcsList = sortedKhoi.filter((k) => getGradeInfo(k.ten_khoi).category === "THCS");
    const thptList = sortedKhoi.filter((k) => getGradeInfo(k.ten_khoi).category === "THPT");
    const khacList = sortedKhoi.filter((k) => getGradeInfo(k.ten_khoi).category === "KHAC");

    let curKhoiRow = 5;
    const writeKhoiRow = (
      name: string,
      qty: number,
      options?: { bold?: boolean; colorArgb?: string }
    ) => {
      const c1 = worksheet.getCell(curKhoiRow, khoiColStart);
      const c2 = worksheet.getCell(curKhoiRow, khoiColStart + 1);

      c1.value = name;
      c2.value = qty;

      const font: Partial<ExcelJS.Font> = {
        name: "Calibri",
        size: 10,
        bold: options?.bold ?? false,
      };
      if (options?.colorArgb) {
        font.color = { argb: options.colorArgb };
      }

      c1.font = font;
      c2.font = font;

      c1.alignment = { horizontal: "left", vertical: "middle" };
      c2.alignment = { horizontal: "right", vertical: "middle" };

      curKhoiRow++;
    };

    // Tiểu học
    if (tihList.length > 0) {
      tihList.forEach((k) => writeKhoiRow(k.ten_khoi, k.so_luong));
      const sumTiH = tihList.reduce((acc, cur) => acc + cur.so_luong, 0);
      writeKhoiRow("Tổng TiH", sumTiH, { bold: true, colorArgb: "FFFF0000" });
    }

    // THCS
    if (thcsList.length > 0) {
      thcsList.forEach((k) => writeKhoiRow(k.ten_khoi, k.so_luong));
      const sumTHCS = thcsList.reduce((acc, cur) => acc + cur.so_luong, 0);
      writeKhoiRow("Tổng THCS", sumTHCS, { bold: true, colorArgb: "FF008000" });
    }

    // THPT
    if (thptList.length > 0) {
      thptList.forEach((k) => writeKhoiRow(k.ten_khoi, k.so_luong));
      const sumTHPT = thptList.reduce((acc, cur) => acc + cur.so_luong, 0);
      writeKhoiRow("Tổng THPT", sumTHPT, { bold: true, colorArgb: "FF0070C0" });
    }

    // Các khối khác
    if (khacList.length > 0) {
      khacList.forEach((k) => writeKhoiRow(k.ten_khoi, k.so_luong));
    }

    // Dòng tổng cộng
    const totalAll = sortedKhoi.reduce((acc, cur) => acc + cur.so_luong, 0);
    writeKhoiRow("Tổng cộng", totalAll, { bold: true });

    // Xuất file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `BaoComTrua_${dateStr}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };


  const columns: IDataGridColumn[] = useMemo(
    (): IDataGridColumn[] => [
      {
        caption: "Mã HS",
        dataField: "ma_hs",
        fixed: "left",
        width: 120,
        exportValue: (data: any) => data.ma_hs || "",
      },
      {
        caption: translate("Học sinh"),
        dataField: "ho_ten",
        fixed: "left",
        width: 200,
        exportValue: (data: any) => data.ho_ten || "",
        cellRender: (data: any) => <HocSinhShortInfo data={data} />,
      },
      {
        caption: "Khối",
        dataField: "ten_khoi",
        align: "center",
        width: 60,
        exportValue: (data: any) => data.ten_khoi || "",
      },
      {
        caption: "Lớp",
        dataField: "ten_lop",
        align: "center",
        width: 60,
        exportValue: (data: any) => data.ten_lop || "",
      },
      {
        caption: "Đợt đăng ký",
        dataField: "dot_dang_ky",
        align: "left",
        width: 180,
        cellRender: (data: any) => <TextWithEllipse text={data.dot_dang_ky} lineNumber={1} />,
        exportValue: (data: any) => data.dot_dang_ky || "",
      },
      {
        caption: "Đăng ký ăn",
        dataField: "ten_ban_tru",
        align: "left",
        width: 180,
        cellRender: (data: any) => <TextWithEllipse text={data.ten_ban_tru} lineNumber={1} />,
        exportValue: (data: any) => data.ten_ban_tru || "",
      },
      {
        caption: "Mã bán trú",
        dataField: "ma_ban_tru",
        align: "left",
        width: 160,
        cellRender: (data: any) => (
          <TextWithEllipse text={data.ma_ban_tru_theo_truong || data.ma_ban_tru} lineNumber={1} />
        ),
        exportValue: (data: any) => data.ma_ban_tru_theo_truong || data.ma_ban_tru || "",
      },
      {
        caption: "Điểm danh",
        dataField: "diem_danh",
        align: "left",
        width: 140,
        cellRender: (data: any) => <TextWithEllipse text={data.diem_danh || "-"} lineNumber={1} />,
        exportValue: (data: any) => data.diem_danh || "-",
      },
    ],
    [translate]
  );

  if (!canView) {
    return (
      <Box sx={{ textAlign: "center", padding: 4 }}>
        <Text text="Base.Message.NoPermission" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 1 }}>
        <Box>
          <Text
            text="Ngày ăn"
            sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default", mr: 2 }}
          />
          <MyTextInput
            type="date"
            sx={{
              width: "180px",
              height: "32px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "border.default",
              "&:focus": {
                borderColor: "accent.emphasis",
                boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)",
              },
            }}
            value={dataLoad.from_date}
            onChange={handleDateChange("from_date")}
          />
        </Box>
        <Box>
          <ComboboxDotDangKyAn
            value={filterDotDangKyId}
            onValueChanged={setFilterDotDangKyId}
            isShowClearButton
            isShowAll
          />
        </Box>
        <Box>
          <ComboboxTruong
            value={filterTruongId}
            onValueChanged={(id) => {
              setFilterTruongId(id);
              setFilterKhoiId(0);
              setFilterHeId(0);
            }}
            isShowClearButton
          />
        </Box>
        <Box sx={!filterTruongId ? { pointerEvents: "none", opacity: 0.5, cursor: "not-allowed" } : {}}>
          <ComboboxKhoi
            disabled={!filterTruongId}
            value={filterKhoiId}
            onValueChanged={(id) => {
              setFilterKhoiId(id);
              setFilterHeId(0);
            }}
            dm_truong_id={filterTruongId || undefined}
            isShowClearButton
          />
        </Box>
        <Box sx={!filterTruongId ? { pointerEvents: "none", opacity: 0.5, cursor: "not-allowed" } : {}}>
          <ComboboxHe
            disabled={!filterKhoiId}
            value={filterHeId}
            onValueChanged={setFilterHeId}
            dm_truong_id={filterTruongId || undefined}
            dm_khoi_id={filterKhoiId || undefined}
            isShowClearButton
          />
        </Box>

        <Box>
          <Button
            text="Tổng hợp"
            leadingVisual={SearchIcon}
            isLoading={loading}
            onClick={handleSearchUserAsync}
            sx={{ height: "32px" }}
          />
        </Box>
      </Box>
      <DataGrid
        keyExpr="ts_hocsinh_id"
        dataSource={filteredData}
        isLoading={loading || status === ePageBaseStatus.is_loading}
        title="Báo cáo ăn theo ngày"
        height={window.innerHeight - 170}
        exportConfig={{
          enable: true,
          fileName: "bao-cao-an-theo-ngay",
        }}
        searchEnable
        actionComponent={
          <Button
            text="Xuất Excel báo cơm"
            leadingVisual={FileDirectoryIcon}
            onClick={handleExportBaoComExcel}
          />
        }
        columns={columns}
        paging={{
          enable: true,
          pageSize: 50,
          pageSizeItems: [50, 200, 500, 1000, 5000],
        }}
      />
    </Box>
  );
};

export default BaoCaoNgayPage;
